import { SupportedUserLanguage } from '@pelckmans/business-components/context/LanguageContext';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const ROUTES = {
  MY_METHODS: {
    route: {
      nl: 'dashboard',
      fr: 'tableau-de-bord',
    },
  },
  MY_GROUPS: {
    route: {
      nl: 'mijn-klassen',
      fr: 'mes-groupes',
    },
  },
  MY_TEACHERS: {
    route: {
      nl: 'mijn-leerkrachten',
      fr: 'mes-professeurs',
    },
  },
  MY_PROFILE: {
    route: {
      nl: 'mijn-account',
      fr: 'mon-compte',
    },
  },
  TERMS_AND_CONDITIONS: {
    route: {
      nl: '/algemene-voorwaarden',
      fr: '/conditions-generales',
    },
  },
  MATERIAL: {
    route: {
      nl: ':subjectSlug/:methodSlug/:moduleSlug',
      fr: ':subjectSlug/:methodSlug/:moduleSlug',
    },
  },
  'MATERIAL.CATEGORICAL': {
    route: {
      nl: 'categorisch',
      fr: 'categorique',
    },
  },
  'MATERIAL.TOC': {
    route: {
      nl: 'materiaal/:selectedModuleSlug/:nodeId?',
      fr: 'materiel/:selectedModuleSlug/:nodeId?',
    },
  },
  'MATERIAL.ASSIGNMENTS': {
    route: {
      nl: 'taken',
      fr: 'tâches',
    },
  },
  'MATERIAL.RESULTS': {
    route: {
      nl: 'resultaten',
      fr: 'resultats',
    },
  },
  'MATERIAL.SEARCH': {
    route: {
      nl: 'zoeken',
      fr: 'rechercher',
    },
  },
} as const;

export function useRoutes() {
  const {
    i18n: { language },
  } = useTranslation();

  /**
   * @description Get the translated route for a key.
   */
  const getRoute = useCallback(
    (route: keyof typeof ROUTES) => ROUTES[route].route[language as SupportedUserLanguage],
    [language],
  );

  /**
   * @description Build relative path based on a route key. Used to build a path to navigate to.
   */
  const buildRelativePath = useCallback(
    (route: keyof typeof ROUTES, params: Record<string, string>) => {
      const basePath = getRoute(route);

      return Object.entries(params).reduce<string>((path, [key, value]) => path.replace(`:${key}`, value), basePath);
    },
    [getRoute],
  );

  /**
   * @description Build absolute path based on a route keys. Used to build a path to navigate to.
   */
  const buildAbsolutePath = useCallback(
    (routes: (keyof typeof ROUTES)[], params: Record<string, string>) => {
      const basePath = routes.map(route => getRoute(route)).join('/');

      return `/ianua/${Object.entries(params).reduce<string>(
        (path, [key, value]) => path.replace(`:${key}`, value),
        basePath,
      )}`;
    },
    [getRoute],
  );

  return { getRoute, buildAbsolutePath, buildRelativePath };
}
